import { useState, useCallback } from "react";
import { ethers } from "ethers";

const MONAD_RPC = "https://rpc.monad.xyz";

const SBT_ADDRESS     = import.meta.env.VITE_SBT_CONTRACT_ADDRESS as string;
const FACTORY_ADDRESS = import.meta.env.VITE_FACTORY_CONTRACT_ADDRESS as string;

const SEL = {
  hasMinted:    "0x38e21cce",
  mintedNameOf: "0x2dcc4827",
  totalTokens:  "0x7e1c0c09",
  getToken:     "0x5ec01e4d",
  currentPrice: "0xeb91d37e",
  estimateBuy:  "0x45f5acd8",
  estimateSell: "0xd6febde8",
  domainUsed:   "0x61164baa",
  balanceOf:    "0x70a08231",
} as const;

const FACTORY_ABI = [
  "function createToken(string,string,uint256,string,string) payable returns (address)",
  "function buyTokens(address) payable",
  "function sellTokens(address,uint256)",
  "function domainUsed(string) view returns (bool)",
  "function totalTokens() view returns (uint256)",
  "function currentPrice(address) view returns (uint256)",
  "function estimateBuy(address,uint256) view returns (uint256)",
  "function estimateSell(address,uint256) view returns (uint256)",
  "function getToken(uint256) view returns (tuple(address tokenContract, string name, string symbol, address creator, string creatorDomain, uint256 totalSupply, uint256 tokensSold, uint256 createdAt, string description, string imageURI))",
];

export interface TokenInfo {
  tokenContract: string;
  name:          string;
  symbol:        string;
  creator:       string;
  creatorDomain: string;
  totalSupply:   bigint;
  tokensSold:    bigint;
  createdAt:     number;
  description:   string;
  imageURI:      string;
  currentPrice?: bigint;
  marketCap?:    bigint;
}

export interface CreateTokenParams {
  name:          string;
  symbol:        string;
  totalSupply:   bigint;
  description:   string;
  imageURI:      string;
  initialBuyMon: bigint;
}

export interface UseSquadTokenReturn {
  checkSBTHolder:     (address: string) => Promise<boolean>;
  getDomainName:      (address: string) => Promise<string>;
  checkDomainUsed:    (domain: string)  => Promise<boolean>;
  getAllTokens:        () => Promise<TokenInfo[]>;
  getTokenPrice:      (tokenAddress: string) => Promise<bigint>;
  estimateBuy:        (tokenAddress: string, monAmount: bigint) => Promise<bigint>;
  estimateSell:       (tokenAddress: string, tokenAmount: bigint) => Promise<bigint>;
  getUserBalance:     (tokenAddress: string, walletAddress: string) => Promise<bigint>;
  getAvailableSupply: (token: TokenInfo) => bigint;
  createToken:        (params: CreateTokenParams) => Promise<string>;
  buyTokens:          (tokenAddress: string, monAmount: bigint) => Promise<string>;
  sellTokens:         (tokenAddress: string, tokenAmount: bigint) => Promise<string>;
  loading:            boolean;
  error:              string | null;
  clearError:         () => void;
}

function getReadProvider(): ethers.JsonRpcProvider {
  return new ethers.JsonRpcProvider(MONAD_RPC);
}

function pad32(hex: string): string {
  return hex.replace("0x", "").padStart(64, "0");
}

function encodeAddress(addr: string): string {
  return pad32(addr.toLowerCase());
}

function encodeUint256(val: bigint): string {
  return pad32(val.toString(16));
}

let rpcId = 1;

async function rpcCall(method: string, params: unknown[]): Promise<unknown> {
  const res = await fetch(MONAD_RPC, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: rpcId++, method, params }),
  });
  const json = await res.json() as { result?: unknown; error?: { message: string } };
  if (json.error) throw new Error(json.error.message);
  return json.result;
}

async function ethCall(to: string, data: string): Promise<string> {
  return rpcCall("eth_call", [{ to, data }, "latest"]) as Promise<string>;
}

function hexToBigInt(hex: string): bigint {
  const clean = hex.replace("0x", "");
  return clean ? BigInt("0x" + clean) : 0n;
}

function decodeBool(data: string): boolean {
  return hexToBigInt(data) !== 0n;
}

function decodeUint256At(data: string, wordIndex: number): bigint {
  const start = 2 + wordIndex * 64;
  return hexToBigInt(data.slice(start, start + 64));
}

function decodeString(data: string, offsetWordIndex: number): string {
  const byteOffset = Number(decodeUint256At(data, offsetWordIndex));
  const charStart  = 2 + byteOffset * 2;
  const strLen     = Number(hexToBigInt(data.slice(charStart, charStart + 64)));
  if (strLen === 0) return "";
  const strData = data.slice(charStart + 64, charStart + 64 + strLen * 2);
  const matched = strData.match(/.{2}/g);
  if (!matched) return "";
  const bytes = new Uint8Array(matched.map((b) => parseInt(b, 16)));
  return new TextDecoder().decode(bytes);
}

function decodeStringResult(data: string): string {
  return decodeString(data, 0);
}

export function useSquadToken(): UseSquadTokenReturn {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const withLoading = useCallback(async <T>(fn: () => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const getSigner = useCallback(async () => {
    if (!window.ethereum) throw new Error("No wallet detected");
    const provider = new ethers.BrowserProvider(window.ethereum);
    return provider.getSigner();
  }, []);

  const checkSBTHolder = useCallback(async (address: string): Promise<boolean> => {
    const data   = SEL.hasMinted + encodeAddress(address);
    const result = await ethCall(SBT_ADDRESS, data);
    return decodeBool(result);
  }, []);

  const getDomainName = useCallback(async (address: string): Promise<string> => {
    const data   = SEL.mintedNameOf + encodeAddress(address);
    const result = await ethCall(SBT_ADDRESS, data);
    return decodeStringResult(result);
  }, []);

  const checkDomainUsed = useCallback(async (domain: string): Promise<boolean> => {
    try {
      const provider = getReadProvider();
      const contract = new ethers.Contract(FACTORY_ADDRESS, FACTORY_ABI, provider);
      return await contract.domainUsed(domain);
    } catch {
      return false;
    }
  }, []);

  const getAllTokens = useCallback(async (): Promise<TokenInfo[]> => {
    const provider = getReadProvider();
    const factory  = new ethers.Contract(FACTORY_ADDRESS, FACTORY_ABI, provider);

    const count = Number(await factory.totalTokens());
    if (count === 0) return [];

    const tokens: TokenInfo[] = [];
    for (let i = 0; i < count; i++) {
      try {
        const t = await factory.getToken(i);
        const info: TokenInfo = {
          tokenContract: t.tokenContract,
          name:          t.name,
          symbol:        t.symbol,
          creator:       t.creator,
          creatorDomain: t.creatorDomain,
          totalSupply:   t.totalSupply,
          tokensSold:    t.tokensSold,
          createdAt:     Number(t.createdAt),
          description:   t.description,
          imageURI:      t.imageURI,
        };
        try {
          const price       = await factory.currentPrice(info.tokenContract);
          info.currentPrice = price;
          info.marketCap    = price * (info.totalSupply / 10n ** 18n);
        } catch {
          info.currentPrice = 0n;
          info.marketCap    = 0n;
        }
        tokens.push(info);
      } catch {
        // skip malformed entry
      }
    }
    return tokens;
  }, []);

  const getTokenPrice = useCallback(async (tokenAddress: string): Promise<bigint> => {
    const calldata = SEL.currentPrice + encodeAddress(tokenAddress);
    const result   = await ethCall(FACTORY_ADDRESS, calldata);
    return hexToBigInt(result);
  }, []);

  const estimateBuyTokens = useCallback(
    async (tokenAddress: string, monAmount: bigint): Promise<bigint> => {
      const calldata = SEL.estimateBuy + encodeAddress(tokenAddress) + encodeUint256(monAmount);
      const result   = await ethCall(FACTORY_ADDRESS, calldata);
      return hexToBigInt(result);
    },
    []
  );

  const estimateSellTokens = useCallback(
    async (tokenAddress: string, tokenAmount: bigint): Promise<bigint> => {
      const calldata = SEL.estimateSell + encodeAddress(tokenAddress) + encodeUint256(tokenAmount);
      const result   = await ethCall(FACTORY_ADDRESS, calldata);
      return hexToBigInt(result);
    },
    []
  );

  const getUserBalance = useCallback(
    async (tokenAddress: string, walletAddress: string): Promise<bigint> => {
      try {
        const calldata = SEL.balanceOf + encodeAddress(walletAddress);
        const result   = await ethCall(tokenAddress, calldata);
        return hexToBigInt(result);
      } catch {
        return 0n;
      }
    },
    []
  );

  const getAvailableSupply = useCallback((token: TokenInfo): bigint => {
    const soldWei = token.tokensSold * 10n ** 18n;
    if (soldWei >= token.totalSupply) return 0n;
    return token.totalSupply - soldWei;
  }, []);

  const createToken = useCallback(
    async (params: CreateTokenParams): Promise<string> => {
      return withLoading(async () => {
        const signer   = await getSigner();
        const contract = new ethers.Contract(FACTORY_ADDRESS, FACTORY_ABI, signer);
        const tx = await contract.createToken(
          params.name,
          params.symbol,
          params.totalSupply,
          params.description,
          params.imageURI,
          { value: params.initialBuyMon }
        );
        return tx.hash;
      });
    },
    [withLoading, getSigner]
  );

  const buyTokens = useCallback(
    async (tokenAddress: string, monAmount: bigint): Promise<string> => {
      return withLoading(async () => {
        const signer   = await getSigner();
        const contract = new ethers.Contract(FACTORY_ADDRESS, FACTORY_ABI, signer);
        const tx = await contract.buyTokens(tokenAddress, { value: monAmount });
        return tx.hash;
      });
    },
    [withLoading, getSigner]
  );

  const sellTokens = useCallback(
    async (tokenAddress: string, tokenAmount: bigint): Promise<string> => {
      return withLoading(async () => {
        const signer   = await getSigner();
        const contract = new ethers.Contract(FACTORY_ADDRESS, FACTORY_ABI, signer);
        const tx = await contract.sellTokens(tokenAddress, tokenAmount);
        return tx.hash;
      });
    },
    [withLoading, getSigner]
  );

  return {
    checkSBTHolder,
    getDomainName,
    checkDomainUsed,
    getAllTokens,
    getTokenPrice,
    estimateBuy:        estimateBuyTokens,
    estimateSell:       estimateSellTokens,
    getUserBalance,
    getAvailableSupply,
    createToken,
    buyTokens,
    sellTokens,
    loading,
    error,
    clearError,
  };
}